import type { Editor } from "@tiptap/react";
import type { ChatFileAttachment } from "@v2/features/chat/utils/chatFileAttachment";

import React, { useEffect, useRef, useState } from "react";

import { Chat } from "@agent-ui/features/chat/components/Chat";
import ChatHeader from "@agent-ui/features/chat/components/chat-elements/ChatHeader";
import ManageChats from "@agent-ui/features/chat/components/chat-elements/ManageChats";
import AssetComponent from "@agent-ui/features/chat/components/chat-elements/message-input/assetNodeExtension";
import IntegrationObjectComponent from "@agent-ui/features/chat/components/chat-elements/message-input/integrationObjectNodeExtension";
import ProductComponent from "@agent-ui/features/chat/components/chat-elements/message-input/productNodeExtension";
import useChatMessageLimits from "@agent-ui/features/chat/hooks/useChatMessageLimits";
import { useCurrentChatContext } from "@agent-ui/features/chat/providers/CurrentChatContext";
import {
  useDisplayMode,
  useModifiersStoreActions,
} from "@agent-ui/features/modifiers/store/ModifiersStoreContext";
import { useIsMobile } from "@replo/design-system/hooks/use-mobile";
import twMerge from "@replo/design-system/utils/twMerge";
import { skipToken } from "@tanstack/react-query";
import Placeholder from "@tiptap/extension-placeholder";
import { useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { marketingClient } from "@trpc/marketing";
import useCurrentProjectId from "@v2/hooks/useCurrentProjectId";
import { useLogAnalytics } from "@v2/hooks/useLogAnalytics";
import { opencodeRouter } from "@v2/trpc/routes/opencode";
import { useLocation, useSearchParams } from "react-router-dom";
import { useRequiredContext } from "replo-utils/react/context";
import { Markdown as TiptapMarkdown } from "tiptap-markdown";

import HardBreakMarkdown from "../extensions/hardBreakMarkdown";
import { useChatSidebar } from "../hooks/useChatSidebar";
import { useSessionActions } from "../hooks/useOpencodeSession";
import { useSessionUuid } from "../hooks/useSessionUuid";
import { useIsSessionStreaming } from "../store/SessionStoreContext";
import {
  chatEditorHandleKeyDown,
  chatEditorHandlePaste,
  getCleanedChatPrompt,
  useChatEditorOnUpdate,
} from "../utils/tiptapUtils";

const useChatAutoSubmitListeners = () => {
  const [autoSubmitData, setAutoSubmitData] = useState<{
    prompt: string;
    shouldSubmit: boolean;
    isSubmitting?: boolean;
    files?: ChatFileAttachment[];
  } | null>(null);
  const logEvent = useLogAnalytics();
  const [, setSearchParams] = useSearchParams();
  const { setFiles, addFiles } = useCurrentChatContext();
  const { sendPrompt } = useSessionActions();
  const sessionUuid = useSessionUuid();
  const location = useLocation();
  const { editor: chatEditor } = useRequiredContext(ChatEditorContext);

  const storedData = localStorage.getItem("replo_marketing_seed");
  if (storedData) {
    const marketingSeedData = JSON.parse(storedData);
    if (marketingSeedData?.prompt) {
      logEvent("marketing.site.prompt", {
        prompt: marketingSeedData.prompt,
        jti: marketingSeedData.jti,
        hasImage: Boolean(marketingSeedData.hasImage),
      });

      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev);
        newParams.set("sessionUuid", "");
        return newParams;
      });
      if (marketingSeedData?.jti && marketingSeedData?.hasImage) {
        void (async () => {
          const { blob: base64Blob, contentType } =
            await marketingClient.getMarketingSeedImage.query({
              jti: marketingSeedData.jti,
            });
          const binaryString = atob(base64Blob);
          const bytes = new Uint8Array(binaryString.length);
          for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
          }
          const blob = new Blob([bytes], { type: contentType });
          const file = new File([blob], "marketing-seed-image", {
            type: contentType,
          });
          await addFiles([file]);

          setAutoSubmitData({
            prompt: marketingSeedData.prompt,
            shouldSubmit: true,
          });
        })();
      } else {
        setAutoSubmitData({
          prompt: marketingSeedData.prompt,
          shouldSubmit: true,
        });
      }

      localStorage.removeItem("replo_marketing_seed");
    }
  }

  // Handle initial message from navigation state
  useEffect(() => {
    const initialData = location.state as {
      initialMessage?: string;
      initialFiles?: ChatFileAttachment[] | null;
    } | null;

    if (initialData?.initialMessage && !sessionUuid) {
      setAutoSubmitData({
        prompt: initialData.initialMessage,
        shouldSubmit: true,
        files: initialData.initialFiles ?? [],
      });

      window.history.replaceState(
        {},
        document.title,
        window.location.pathname + window.location.search,
      );
    }
  }, [location.state, sessionUuid]);

  // NOTE (Bobby, 2025-07-30): Have this as a separate useEffect since we need to wait for the files state to be set before submitting.
  // NOTE (Kevin, 2025-08-25): Prompt actions are intentionally not included as dependencies
  // because including action callbacks here previously caused auto-submit loops.
  // We mark as submitting to prevent re-triggering while preserving data for retry on failure
  // biome-ignore lint/correctness/useExhaustiveDependencies: we want to prevent infinite loops
  useEffect(() => {
    if (
      autoSubmitData?.shouldSubmit &&
      !autoSubmitData.isSubmitting &&
      chatEditor
    ) {
      const autoSubmitPrompt = autoSubmitData.prompt;
      const autoSubmitFiles = autoSubmitData.files ?? [];

      setAutoSubmitData({
        prompt: autoSubmitPrompt,
        shouldSubmit: true,
        isSubmitting: true,
        files: autoSubmitFiles,
      });

      void sendPrompt({
        prompt: autoSubmitPrompt,
        files: autoSubmitFiles ?? [],
      }).then(() => {
        // NOTE (Ben, 2025-12-12): Removed chatEditor.commands.setContent("", false) here.
        // The auto-submitted prompt comes from navigation state, not the editor, so clearing
        // the editor after streaming completes would wipe any text the user typed while waiting.
        setFiles([]);
        setAutoSubmitData(null);
      });
    }
  }, [autoSubmitData, chatEditor]);
};

const ChatAutoSubmitListener = () => {
  useChatAutoSubmitListeners();
  return null;
};

export const ChatEditorContext = React.createContext<{
  editor: Editor | null;
}>({
  editor: null,
});

const ChatEditorProvider = ({
  children,
  onSubmit,
}: React.PropsWithChildren<{
  onSubmit: (chatEditor: Editor) => void;
}>) => {
  const { addFiles } = useCurrentChatContext();
  const chatEditorOnUpdate = useChatEditorOnUpdate();
  const chatEditor = useEditor({
    extensions: [
      StarterKit.configure({
        hardBreak: false,
      }),
      AssetComponent,
      ProductComponent,
      IntegrationObjectComponent,
      HardBreakMarkdown,
      TiptapMarkdown.configure({
        /*
         * NOTE (Kevin 2025-09-24):
         * breaks=false because the HardBreak extension emits <br /> directly.
         * html=true keeps <chat-token> placeholders intact through markdown.
         * tightLists=true prevents blank paragraphs between list items.
         */
        breaks: false,
        html: true,
        tightLists: true,
      }),
      Placeholder.configure({
        placeholder: "Ask for any edits or customizations",
      }),
    ],
    content: "",
    onUpdate: chatEditorOnUpdate,
    onCreate: ({ editor }) => {
      editor.commands.focus();
    },
    editorProps: {
      attributes: {
        style: "font-size: 14px; white-space: pre-wrap;",
        class: "react-editor-chat",
      },
      handleKeyDown: chatEditorHandleKeyDown(() => {
        if (!chatEditor) {
          return;
        }
        onSubmit(chatEditor);
      }),
      handlePaste: chatEditorHandlePaste(addFiles),
    },
  });

  return (
    <ChatEditorContext.Provider value={{ editor: chatEditor }}>
      {children}
    </ChatEditorContext.Provider>
  );
};

export default function ReactEditorChat() {
  const { files, setFiles } = useCurrentChatContext();
  const { sendPrompt } = useSessionActions();
  const sessionUuid = useSessionUuid();

  const { hasReachedLimit } = useChatMessageLimits();

  const { open: isChatSidebarOpen, setOpen: setIsChatSidebarOpen } =
    useChatSidebar();
  const isMobile = useIsMobile();

  const displayMode = useDisplayMode();
  const modifierActions = useModifiersStoreActions();

  const projectId = useCurrentProjectId();

  const { data: session } = opencodeRouter.getSession.useQuery(
    sessionUuid && projectId ? { sessionUuid, projectId } : skipToken,
  );

  const isChatLoading = useIsSessionStreaming(sessionUuid);

  // NOTE (Ben, 2025-12-05): This ref is populated by Chat.tsx with a submit handler that includes
  // scroll-to-bottom behavior. We need this because there are two submit paths:
  // 1. Enter key → ChatEditorProvider.onSubmit (bypasses MessageInput's form)
  // 2. Button click → MessageInput's form onSubmit
  // The ref allows both paths to use the same scroll-enabled handler defined in Chat.tsx.
  const onSubmitRef = useRef<
    | ((opts: { chatEditor: Editor; event: React.FormEvent | null }) => void)
    | null
  >(null);

  const handleSubmit = async (opts: {
    chatEditor: Editor;
    event: React.FormEvent | null;
  }) => {
    const { chatEditor, event } = opts;
    event?.preventDefault();

    if (!chatEditor || isChatLoading) {
      return;
    }

    if (hasReachedLimit) {
      return;
    }
    const currentPrompt = getCleanedChatPrompt(chatEditor);
    chatEditor.commands.setContent("", false);
    setFiles([]);

    setIsChatSidebarOpen(false);

    if (displayMode === "select") {
      modifierActions.setDisplayMode("interactive");
    }
    await sendPrompt({ prompt: currentPrompt, files });
  };

  return (
    <div className="flex w-full h-full min-h-0 flex-row shadow-2xs border-0 sm:border-0.5 border-border sm:rounded-lg bg-white relative overflow-visible">
      <style>
        {`
          .react-editor-chat.tiptap p.is-editor-empty:first-child::before {
            color: var(--color-neutral-500);
            content: attr(data-placeholder);
            float: left;
            height: 0;
            pointer-events: none;
          }
          .react-editor-chat.tiptap.ProseMirror-focused p.is-editor-empty:first-child::before {
            color: var(--color-neutral-400);
          }
          .react-editor-chat.tiptap ol {
            list-style-type: decimal;
            list-style-position: outside;
            padding-left: 1.5rem;
          }
          .react-editor-chat.tiptap ul {
            list-style-type: disc;
            list-style-position: outside;
            padding-left: 1.5rem;
          }
        `}
      </style>

      {/* Left Chat List (desktop only; mobile handled by layout-level dock) */}
      {!isMobile && (
        <div
          data-state={isChatSidebarOpen ? "expanded" : "collapsed"}
          aria-hidden={!isChatSidebarOpen}
          className={twMerge(
            "flex h-full flex-shrink-0 flex-col overflow-hidden transition-[width,opacity] duration-300 ease-out data-[state=collapsed]:w-0 data-[state=collapsed]:opacity-0",
            "w-[250px] border-r-0.5 border-border rounded-tl-lg rounded-bl-lg",
            !isChatSidebarOpen && "border-r-transparent",
          )}
        >
          <div className="flex flex-col min-w-[250px] h-full">
            <ManageChats />
          </div>
        </div>
      )}

      {/* Main Chat Panel */}
      <div className="flex-1 flex flex-col h-full min-h-0 items-center overflow-hidden">
        <div className="hidden md:flex w-full justify-start p-3 bg-white rounded-t-lg border-b-0.5 border-border min-h-14">
          <ChatHeader
            chatTitle={session?.title}
            isGeneratingTitle={Boolean(isChatLoading) && !session?.title}
          />
        </div>
        <div className="p-3 flex flex-col gap-4 h-full min-h-0 max-w-full sm:max-w-2xl lg:max-w-3xl w-full relative overflow-hidden">
          <ChatEditorProvider
            onSubmit={(editor) => {
              const submitHandler = onSubmitRef.current ?? handleSubmit;
              void submitHandler({ chatEditor: editor, event: null });
            }}
          >
            <Chat
              handleSubmit={(opts) => void handleSubmit(opts)}
              onSubmitRef={onSubmitRef}
            />
            <ChatAutoSubmitListener />
          </ChatEditorProvider>
        </div>
      </div>
    </div>
  );
}
